import type {
  AgentMetadata,
  AgentSidePanelLayoutMode,
  ResolvedWidgetState,
} from '../model/agent-config.types';

/**
 * Map of agentId → metadata. Composition root (`lib/config/agents.config.ts`)
 * provides this map; entity helpers operate on it without knowing where it
 * comes from. This keeps the entity free of cyclic imports.
 */
export type AgentMetadataMap = Record<string, AgentMetadata | undefined>;

/**
 * Resolves metadata for an agent ID, checking exact matches first then
 * prefix matches (for dynamic agent IDs like `audience-flow-persona-xxx`).
 */
const resolveAgentMetadata = (
  metadata: AgentMetadataMap,
  agentId: string,
): AgentMetadata | undefined => {
  const exactMatch = metadata[agentId];
  if (exactMatch && !exactMatch.isPrefixKey) {
    return exactMatch;
  }

  // Prefix match (for dynamic agent IDs)
  for (const [key, config] of Object.entries(metadata)) {
    if (config?.isPrefixKey && agentId.startsWith(key)) {
      return config;
    }
  }
  return undefined;
};

/**
 * Returns whether the agent's description should be shown in the avatar.
 */
const getAgentShowDescription = (metadata: AgentMetadataMap, agentId: string): boolean => {
  return resolveAgentMetadata(metadata, agentId)?.enableDescription ?? false;
};

/**
 * Returns the chat suggestions for the given agent.
 */
const getAgentSuggestions = (metadata: AgentMetadataMap, agentName: string): string[] => {
  return metadata[agentName]?.suggestions ?? [];
};

/**
 * Returns the human-readable title for the given agent, falling back to
 * the agent name itself if no title is configured.
 */
const getAgentTitle = (metadata: AgentMetadataMap, agentName: string): string => {
  return metadata[agentName]?.title ?? agentName;
};

/**
 * Returns whether suggestions should be auto-fetched after a chat message.
 * Defaults to `true` when not specified.
 */
const getAgentAutoFetchSuggestions = (metadata: AgentMetadataMap, agentName: string): boolean => {
  return metadata[agentName]?.autoFetchSuggestions ?? true;
};

/**
 * Returns the auto-greeting message for an agent (the message auto-sent
 * when a chat is first created).
 */
const getAgentAutoGreeting = (
  metadata: AgentMetadataMap,
  agentName: string,
): string | undefined => {
  return metadata[agentName]?.autoGreeting;
};

/**
 * Returns true if the agent renders only as a widget (no chat UI). The
 * layout switcher hides itself when this is true.
 */
const getIsWidgetOnly = (metadata: AgentMetadataMap, agentId: string): boolean => {
  return resolveAgentMetadata(metadata, agentId)?.widgetOnly ?? false;
};

/**
 * Resolves the initial widget state for an agent, normalizing the
 * `autoOpen: boolean | { layoutMode }` union into flat `{ autoOpen,
 * layoutMode, data }`.
 */
const getInitialWidgetState = (
  metadata: AgentMetadataMap,
  agentId: string,
): ResolvedWidgetState => {
  const config = resolveAgentMetadata(metadata, agentId)?.initialWidgetState;
  const autoOpenConfig = config?.autoOpen;

  let autoOpen = false;
  let layoutMode: NonNullable<AgentSidePanelLayoutMode> = 'split';

  if (typeof autoOpenConfig === 'boolean') {
    autoOpen = autoOpenConfig;
  } else if (autoOpenConfig && typeof autoOpenConfig === 'object') {
    autoOpen = true;
    layoutMode = autoOpenConfig.layoutMode;
  }

  return {
    autoOpen,
    layoutMode,
    data: config?.data ? { ...config.data, agentName: agentId } : null,
  };
};

/**
 * Builds a set of pre-bound lookup helpers for the given metadata map.
 * Composition root invokes this once with its concrete `agentMetadata`
 * map; consumers (e.g. `features/chats`) consume the bound helpers
 * without knowing about the underlying map.
 */
export const makeAgentMetadataHelpers = (metadata: AgentMetadataMap) => ({
  resolveAgentMetadata: (agentId: string) => resolveAgentMetadata(metadata, agentId),
  getAgentShowDescription: (agentId: string) => getAgentShowDescription(metadata, agentId),
  getAgentSuggestions: (agentName: string) => getAgentSuggestions(metadata, agentName),
  getAgentTitle: (agentName: string) => getAgentTitle(metadata, agentName),
  getAgentAutoFetchSuggestions: (agentName: string) =>
    getAgentAutoFetchSuggestions(metadata, agentName),
  getAgentAutoGreeting: (agentName: string) => getAgentAutoGreeting(metadata, agentName),
  getIsWidgetOnly: (agentId: string) => getIsWidgetOnly(metadata, agentId),
  getInitialWidgetState: (agentId: string) => getInitialWidgetState(metadata, agentId),
});

export type AgentMetadataHelpers = ReturnType<typeof makeAgentMetadataHelpers>;
